import { EnquiryRequestService, FeaturesService, AssetOwnerService } from 'client/services';
import GeoJSON from 'ol/format/GeoJSON';
import { getSortedAoEnhancedMeasures } from 'client/utils/helpers/Enquiry/Enquiry';

const loader = async ({ params, request }) => {
    const url = new URL(request.url);
    const isConfirmation = url.searchParams.get('confirmation') === 'true';
    const enquiry = await EnquiryRequestService.getEnquiryRequest(params.enquiryId);
    const activityTypes = await EnquiryRequestService.getActivityTypes();
    const enquiryId = enquiry?.enquiryId ?? params.enquiryId;
    let affectedAssetOwners = [];
    let notificationStatuses = [];

    // When viewing confirmation page (just created), only fetch affected asset owners
    // When viewing an existing enquiry, only fetch notification statuses
    if (isConfirmation) {
        // Fetch affected asset owners for this enquiry
        // This returns a list of organisations that have assets within the enquiry's area of interest
        // However, this API response does NOT include contact details for the organisations
        try {
            const response = await EnquiryRequestService.getAffectedAssetOwners(enquiryId);
            // Normalise response so the page always receives an array
            affectedAssetOwners = Array.isArray(response) ? response : response?.assetOwners ?? [];
        } catch (error) {
            affectedAssetOwners = [];
        }
    } else {
        // Fetch notification statuses for organisations that have been notified
        // This is used when viewing an existing enquiry
        try {
            const response = await EnquiryRequestService.getNotificationStatuses(enquiryId);
            notificationStatuses = Array.isArray(response)
                ? response
                : response?.assetOwners ?? response?.notificationStatuses ?? [];
        } catch (error) {
            notificationStatuses = [];
        }
    }

    if (enquiry.geometry) {
        // When geometry exists we can derive features, affected asset owners, and sorted enhanced measures.
        const aoi = new GeoJSON().readFeature({ geometry: enquiry.geometry, type: 'Feature' });
        const enquiryFeatures = await FeaturesService.getEnquiryFeatures(enquiry.geometry);

        const dataProviderIds = enquiryFeatures.featureIdList.map(featureId => featureId?.dataProviderId);

        // Fetch asset owners from AssetOwnerService - THIS IS WHERE CONTACT DETAILS COME FROM
        // The AssetOwnerService.getAssetOwners() returns organisation data including:
        // - operationalZone: organisations in the operational zone
        // - assetsInAOIOwners: organisations with assets in the area of interest
        // Both arrays contain contact details (business and emergency contacts) for each organisation
        //
        // These contacts are then reconciled with the affectedAssetOwners list by matching
        // organisation IDs across the different data sources (see AffectedAssetOwnersNotification component)
        const assetOwners = await AssetOwnerService.getAssetOwners(aoi, dataProviderIds);
        const sortedAoEnhancedMeasures = getSortedAoEnhancedMeasures(assetOwners?.assetsInAOIOwners, enquiryFeatures?.featuresByAssetType) ?? [];

        return {
            // Available to the page: raw enquiry, asset owners, affectedAssetOwners, sorted AO enhanced measure, notification statuses
            enquiry,
            activityTypes,
            assetOwners,
            affectedAssetOwners,
            sortedAoEnhancedMeasures,
            notificationStatuses,
            featuresByAssetType: enquiryFeatures?.featuresByAssetType
        };
    }

    return {
        // Without geometry we still provide a consistent payload shape with sensible fallbacks
        enquiry,
        activityTypes,
        featuresByAssetType: {},
        featureIdList: [],
        assetOwners: { assetsInAOIOwners: [] },
        sortedAoEnhancedMeasures: [],
        affectedAssetOwners,
        notificationStatuses

    };
};

export default loader;
